import { ObjectCustomField } from "@/backend/models/customFields/objectCustomField";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { ParameterInputType } from "@/types/parameterTypes";
import { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Form } from "react-bootstrap";
import { PropertyEdit } from "../customFIelds/customFieldObjects/PropertyEdit";
import { Parameter } from "@/backend/models/parameter";
import { NeutralDeleteButton } from "../buttons/NeutralDeleteButton";
import { ObjectKeyVisibility } from "@/types/customFieldTypes";

export function ObjectListEdit(props: { parameter: Parameter, runView: boolean }) {
  const { parameter, runView } = props;
  const deleteField = useParameterStore(state => state.deleteCustomField);

  const choiceOnRunView = runView && parameter.isChoice();
  const canDelete = parameter.inputType !== ParameterInputType.FREE_FORM && !choiceOnRunView;

  const fields = parameter.fields.filter(field => {
    if (!runView) return true;

    if (!parameter.isChoice()) return true;

    if (parameter.propertyKeys.some(property => property.visibility === ObjectKeyVisibility.EDITABLE)) {
      return true;
    }

    return field.isCustomChoice;
  });

  return (
    <div className='d-flex flex-column gap-3'>
      {fields.filter(field => field instanceof ObjectCustomField).map(field => (
        <div key={field.id} className='light-card'>
          <div className='light-card-header d-flex justify-content-between align-items-center'>
            {I18n.get('object')}
            { canDelete &&
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={parameter.fields.length === 1 ? <Tooltip>{I18n.get('cannotDeleteLastItem')}</Tooltip> : <div/>}
              >
                <div>
                  <NeutralDeleteButton
                    onSubmit={() => deleteField(field)}
                    itemType={I18n.get('object')}
                    title={field.objectInstance.title}
                    disabled={parameter.fields.length === 1}
                  />
                </div>
              </OverlayTrigger>
            }
          </div>
          <div className='light-card-body'>
            <ObjectTitle field={field} />
            <div className='d-flex flex-column gap-3'>
              {field.objectInstance.properties.map(property => <PropertyEdit
                key={property.keyId}
                property={property}
                runView={runView}
                customField={field}
              />)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ObjectTitle(props: { field: ObjectCustomField }) {
  const { field } = props;
  const updateObjectInstance = useParameterStore(state => state.updateObjectInstance);
  const [titleError, setTitleError] = useState('');
  const [title, setTitle] = useState(field.objectInstance.title);

  useEffect(() => {
    setTitle(field.objectInstance.title);
  }, [field.objectInstance.title]);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    setTitleError(newTitle.length === 0 ? I18n.get('requiredFieldError', { field: I18n.get('title') }) : '');
  };

  return (
    <div className='mb-3'>
      <Form.Label className='form-label-neutral'>{I18n.get('title')}</Form.Label>
      <Form.Control
        size='sm'
        aria-label={I18n.get('enterField', { field: I18n.get('title') })}
        value={title}
        isInvalid={!!titleError}
        onChange={(event) => handleTitleChange(event.target.value)}
        onBlur={() => void updateObjectInstance(title, field)}
        className="form-control-white"
      />
      <Form.Control.Feedback type="invalid">
        { titleError }
      </Form.Control.Feedback>
    </div>
  );
}
